import { useCallback, useMemo } from "react";

import { StorageKeys } from "./storageKeys";

import { useLocalStorage } from "@/service/Utilities";

function sanitizeGroupIds(raw: unknown): number[] {
	if (!Array.isArray(raw)) return [];

	const uniqueIds = new Set<number>();
	for (const value of raw) {
		const parsed = typeof value === "number" ? value : Number(value);
		if (!Number.isInteger(parsed) || parsed <= 0) continue;
		uniqueIds.add(parsed);
	}
	return [...uniqueIds];
}

/**
 * Persisted selected task group IDs for Active/Recurring tasks.
 * Sanitizes stored values (filters to valid numbers only).
 */
export function useSelectedTaskGroups(): [
	number[],
	React.Dispatch<React.SetStateAction<number[]>>,
] {
	const [raw, setRaw] = useLocalStorage<unknown>(
		StorageKeys.selectedGroupIds,
		[],
	);

	const sanitized = useMemo(() => sanitizeGroupIds(raw), [raw]);

	const setSanitized = useCallback<
		React.Dispatch<React.SetStateAction<number[]>>
	>(
		(next) => {
			setRaw((prev) => {
				const resolved =
					typeof next === "function" ? next(sanitizeGroupIds(prev)) : next;
				return sanitizeGroupIds(resolved);
			});
		},
		[setRaw],
	);

	return [sanitized, setSanitized];
}
